import { Observable } from 'rxjs';
import { find } from 'rxjs/operators';
import { UTCDate } from 'types';

export class ValidationRules {
  static _image(): any {
    return [
      'required',
      {
        nested_object: {
          scanned: ['required', { one_of: 'true' }],
        },
      },
    ];
  }

  static _fingerprints(): any {
    return [
      'required',
      {
        nested_object: {
          hash: 'required',
          cert: 'required',
          ekey: 'required',
          digestURN: 'required',
          transportURN: 'required',
          items: [
            'required',
            {
              list_of_objects: [
                {
                  scanned: ['required', { one_of: ['0', '1', '2', '3'] }],
                },
              ],
            },
          ],
        },
      },
    ];
  }

  static _enum(visible: 'AlwaysVisible' | 'CanHide', ...values: string[]): any {
    return [
      ...(visible === 'AlwaysVisible' ? ['required'] : []),
      {
        nested_object: {
          value: ['required', { one_of: values }],
        },
      },
    ];
  }

  static _bool(visible: 'AlwaysVisible' | 'CanHide', ...values: string[]): any {
    return [...(visible === 'AlwaysVisible' ? ['required'] : []), { one_of: values }];
  }

  static _date(visible: 'AlwaysVisible' | 'CanHide', ...rules: any[]): any {
    return [
      ...(visible === 'AlwaysVisible' ? ['required'] : []),
      {
        nested_object: {
          value: ['required', ...rules],
        },
      },
    ];
  }

  static _odate(visible: 'AlwaysVisible' | 'CanHide', ...rules: any[]): any {
    return [
      ...(visible === 'AlwaysVisible' ? ['required'] : []),
      {
        nested_object: {
          value: ['string', ...rules],
        },
      },
    ];
  }

  static _nullable_date(visible: 'AlwaysVisible' | 'CanHide', ...rules: any[]): any {
    return [
      ...(visible === 'AlwaysVisible' ? ['required'] : []),
      {
        nested_object: {
          value: ['not_empty', ...rules],
        },
      },
    ];
  }

  static _string(visible: 'AlwaysVisible' | 'CanHide', ...rules: any[]): any {
    return [visible === 'AlwaysVisible' ? 'required' : 'not_empty', ...rules];
  }

  static _ostring(visible: 'AlwaysVisible' | 'CanHide', ...rules: any[]): any {
    return ['string', ...rules];
  }

  static _nullable_string(visible: 'AlwaysVisible' | 'CanHide', ...rules: any[]): any {
    return [
      ...(visible === 'AlwaysVisible' ? ['required'] : []),
      {
        nested_object: {
          value: ['not_empty', ...rules],
        },
      },
    ];
  }

  static _onullable_string(visible: 'AlwaysVisible' | 'CanHide', ...rules: any[]): any {
    return [
      ...(visible === 'AlwaysVisible' ? ['required'] : []),
      {
        nested_object: {
          value: ['string', ...rules],
        },
      },
    ];
  }

  static _list(inner: any, ...rules: any[]): any {
    return [
      'required',
      {
        list_of_objects: [inner],
      },
      ...rules,
    ];
  }

  static _hideable_list(inner: any, ...rules: any[]): any {
    return [
      {
        list_of_objects: [inner],
      },
      ...rules,
    ];
  }

  static _object(inner: any, ...rules: any[]): any {
    return [
      'required',
      {
        nested_object: inner,
      },
      ...rules,
    ];
  }

  static _hideable_object(inner: any, ...rules: any[]): any {
    return [
      {
        nested_object: inner,
      },
      ...rules,
    ];
  }

  static _variable_object(type: string, inner: any, ...rules: any[]): any {
    return [
      'required',
      {
        variable_object: [type, inner],
      },
      ...rules,
    ];
  }

  static _hideable_variable_object(type: string, inner: any, ...rules: any[]): any {
    return [
      {
        variable_object: [type, inner],
      },
      ...rules,
    ];
  }

  static today(root: any): UTCDate {
    return root['#TODAY#'];
  }

  static fetchNode(root: any, parent: any, path: string[]): any {
    let node = root;
    if (path[0].match(/^\^+$/)) {
      node = parent;
      for (let i = 1; i < path[0].length; ++i) {
        node = node === undefined ? undefined : node['^'];
      }
      path = path.slice(1);
    }
    const result = path.reduce((pv, cv) => (pv === undefined ? undefined : pv[cv]), node);
    return result;
  }

  static checkExists<T>(cache: Observable<T>, predicate: (x: T) => boolean): boolean {
    let result = false;
    cache.pipe(find((x) => predicate(x))).subscribe((x) => (result = !!x));
    return result;
  }

  static offsetDate(x: UTCDate, y: number, m: number, d: number, roundUp: boolean): UTCDate {
    if (y) {
      const old = x.getDate();
      x = x.addYears(y);
      if (x.getDate() !== old) {
        x = x.addDays((roundUp ? 1 : 0) - x.getDate());
      }
    }
    if (m) {
      const old = x.getDate();
      x = x.addMonths(m);
      if (x.getDate() !== old) {
        x = x.addDays((roundUp ? 1 : 0) - x.getDate());
      }
    }
    if (d) {
      x = x.addDays(d);
    }
    return x;
  }

  static utc_date(params: any) {
    const result = function (value: string, root: any, parent: any) {
      if (value === undefined || value === null || value === '') {
        return;
      }
      if (typeof value !== 'string') {
        return 'FORMAT_ERROR';
      }
      if (!UTCDate.iso(value)) {
        return 'FORMAT_ERROR';
      }
      const verify = function () {
        const y = +value.substr(0, 4);
        const m = +value.substr(5, 2);
        const d = +value.substr(8, 2);
        if (params.dmy && y > 0 && m > 0 && d > 0) {
          return true;
        }
        if (params.my && y > 0 && m > 0 && d === 0) {
          return true;
        }
        if (params.y && y > 0 && m === 0 && d === 0) {
          return true;
        }
        return false;
      };
      if (!verify()) {
        return 'FORMAT_ERROR';
      }
      const fetchEdgeObj = function (edge: string, roundUp: boolean) {
        if (edge === 'today') {
          return ValidationRules.today(root);
        }
        if (edge.includes('-')) {
          return UTCDate.iso(edge, roundUp);
        }
        const edgePath = edge.split('.');
        const edgeValue = ValidationRules.fetchNode(root, parent, edgePath);
        if (edgeValue === undefined || edgeValue === null || edgeValue === '') {
          return null;
        }
        if (typeof edgeValue !== 'string') {
          return 'FORMAT_ERROR';
        }
        const obj = UTCDate.iso(edgeValue, roundUp);
        if (!obj) {
          return 'FORMAT_ERROR';
        }
        return obj;
      };
      const offsetDateHelper = function (obj: UTCDate, offset: any, roundUp: boolean): UTCDate {
        return offset ? ValidationRules.offsetDate(obj, offset.y, offset.m, offset.d, roundUp) : obj;
      };
      for (const range of params.range) {
        const p = offsetDateHelper(UTCDate.iso(value, !!range.up_val)!, range.offset_val, !!range.up_val);
        if (range.min) {
          const q = fetchEdgeObj(range.min, !!range.up_edge);
          if (typeof q === 'string') {
            return q;
          }
          if (q instanceof UTCDate && p < offsetDateHelper(q, range.offset_edge, !!range.up_edge)) {
            return range.err || 'NOT_ALLOWED_VALUE';
          }
        }
        if (range.max) {
          const q = fetchEdgeObj(range.max, !!range.up_edge);
          if (typeof q === 'string') {
            return q;
          }
          if (q instanceof UTCDate && p > offsetDateHelper(q, range.offset_edge, !!range.up_edge)) {
            return range.err || 'NOT_ALLOWED_VALUE';
          }
        }
      }
    };
    return result;
  }

  static max_length_sum(params: any) {
    const withPath = params.with.split('.');
    const result = function (value: string, root: any, parent: any): any {
      if (value === undefined || value === null || value === '') {
        return;
      }
      if (typeof value !== 'string') {
        return 'FORMAT_ERROR';
      }
      const withValue = ValidationRules.fetchNode(root, parent, withPath);
      if (withValue === undefined || withValue === null || withValue === '') {
        return;
      }
      if (typeof withValue !== 'string') {
        return 'FORMAT_ERROR';
      }
      const len = value.length + withValue.length;
      if (len > params.sum) {
        return 'TOO_LONG_SUM';
      }
    };
    return result;
  }

  static for_warning(params: any) {
    const tag = params.tag;
    const result = function (value: string) {
      if (value === undefined || value === null || value === '') {
        return tag;
      }
    };
    return result;
  }

  static like_ex(params: any) {
    const tag = params.tag;
    const regexp = new RegExp(params.regexp);
    const result = function (value: string) {
      if (value === undefined || value === null || value === '') {
        return;
      }
      if (typeof value !== 'string') {
        return 'FORMAT_ERROR';
      }
      if (!value.match(regexp)) {
        return tag;
      }
    };
    return result;
  }

  static email() {
    const result = function (value: string): any {
      if (value === undefined || value === null || value === '') {
        return;
      }
      if (typeof value !== 'string') {
        return 'FORMAT_ERROR';
      }
      if (!value.match('^.+@.+\\..+$')) {
        return 'WRONG_STRUCTURE';
      }
      if (value[0] === '.') {
        return 'LEADING_OR_TRAILING_DOT';
      }
      const pos = value.indexOf('@');
      if (pos > 0 && value[pos - 1] === '.') {
        return 'LEADING_OR_TRAILING_DOT';
      }
    };
    return result;
  }
}
